import { useMemo } from 'react';
import { AdvancedSearchResponse } from 'src/serverProxy/useTableData';
import { useAppSelector } from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import _get from 'lodash/get';
import { useCreateAtlasV2BaseMetricRequestBuilder } from 'src/components/AtlasV2Components/Hooks/useAtlasV2BaseMetricRequestBuilder';
import { UseAdvancedSearchDataArgs } from 'src/serverProxy/useAdvancedSearchData';

/**
 * Accepts an arbitrary number of index/field name combinations to return the
 * combined data in a single array where each item is an object containing
 * the value and comparison value for each field.
 */
export default function useAtlasV2AdvancedSearchData({
  fields,
  groupByField,
  indexName,
  requestBuilder,
  requestId = 'advancedSearchData',
  returnRawResponse = false,
  includeRawResponse,
  shouldFetchComparison = true,
  primaryGenericAdvancedSearchArgs = {},
  comparisonGenericAdvancedSearchArgs = {},
  addFieldsFromPrimaryResponse
}: UseAdvancedSearchDataArgs) {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const createBaseMetricRequestBuilder = useCreateAtlasV2BaseMetricRequestBuilder();

  /**
   * There will be two objects in the request - one for
   * the current time period and one for the comparison time period. This is the
   * array of request bodies that will be sent to the server.
   */
  const primaryRequestBuilder = useMemo(() => {
    const builder = createBaseMetricRequestBuilder({
      fields,
      indexName,
      requestId,
      groupByFieldName: groupByField
    })
      .addConditionRangeFilter('weekId', startWeek, endWeek)
      .setPageSize(100)
      .modifyAggregation((aggBuilder) => aggBuilder.addConditionRangeFilter('weekId', startWeek, endWeek));

    if (requestBuilder) {
      requestBuilder(builder, false, fields);
    }

    return builder;
  }, [createBaseMetricRequestBuilder, fields, indexName, requestId, groupByField, startWeek, endWeek, requestBuilder]);

  // Primary data
  const { data: response, isLoading } = useGenericAdvancedSearch<AdvancedSearchResponse<any>[]>({
    requestBody: [primaryRequestBuilder.build()],
    queryKeys: [primaryRequestBuilder.build()],
    requestId,
    ...primaryGenericAdvancedSearchArgs
  });

  // Comparison request bodies - we need to add the fields returned from the primary data as
  // term filters in order to get the data for the same set
  const comparisonRequestBuilder = useMemo(() => {
    const builder = primaryRequestBuilder
      .clone()
      .clearConditionRangeFilters()
      .addConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek); // Comparison date range

    if (addFieldsFromPrimaryResponse) {
      builder.addConditionTermFilter(
        groupByField,
        'should',
        // Add in the field IDs from the primary data as term filters
        _get(response, ['data', 0, 'aggregations', `by_${groupByField}`], []).map((row) => row.fieldId)
      );
    }
    builder.modifyAggregation((aggBuilder) =>
      aggBuilder.replaceConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek)
    );

    if (requestBuilder) {
      requestBuilder(builder, true, fields);
    }

    return builder;
  }, [
    primaryRequestBuilder,
    comparisonStartWeek,
    comparisonEndWeek,
    addFieldsFromPrimaryResponse,
    requestBuilder,
    groupByField,
    response,
    fields
  ]);

  // Secondary data request
  const { data: comparisonResponse, isLoading: compareLoading } = useGenericAdvancedSearch<
    AdvancedSearchResponse<any>[]
  >({
    shouldPerformFetch: !isLoading && shouldFetchComparison,
    queryKeys: [comparisonRequestBuilder.build()],
    requestBody: [comparisonRequestBuilder.build()],
    requestId: `${requestId}_comparison`,
    ...comparisonGenericAdvancedSearchArgs
  });

  /**
   * Merge the primary and comparison data for each index into a single array
   */
  const parsedData: AdvancedSearchParsedDatum[] = useMemo(() => {
    if (returnRawResponse) {
      return null;
    }

    if (isLoading || compareLoading) {
      return [];
    }

    const responseBodies = _get(response, 'data', []);
    const comparisonResponseBodies = _get(comparisonResponse, 'data', []);

    const primaryData = _get(responseBodies, [0, 'aggregations', `by_${groupByField}`], []);
    const comparisonData = _get(comparisonResponseBodies, [0, 'aggregations', `by_${groupByField}`], []);

    // Since comparison data won't be in the same order as primary, we map the field to its data
    // so we can combine primary and secondary data in constant time
    const comparisonDataMap = comparisonData.reduce((acc, row) => {
      return {
        ...acc,
        [row.fieldId]: row
      };
    }, {});

    // Fields might be missing from either the primary or comparison data, so we need to combine them
    const allFieldIds = new Set([
      ...primaryData.map((row) => row.fieldId),
      ...comparisonData.map((row) => row.fieldId)
    ]);

    // Combine primary and comparison data into a single array
    return Array.from(allFieldIds).map((fieldId) => {
      const primaryRow = primaryData.find((row) => row.fieldId === fieldId) || {};
      const comparisonRow = comparisonDataMap[fieldId] || {};

      return {
        fieldId,
        values: primaryRow.additionalValues || {},
        comparisonValues: comparisonRow.additionalValues || {},
        metadata: primaryRow.additionalMetaData || comparisonRow.additionalMetaData || {},
        comparisonMetadata: comparisonRow.additionalMetaData || {},
        rawResponse: includeRawResponse ? { primary: responseBodies, comparison: comparisonResponseBodies } : null
      };
    });
  }, [compareLoading, comparisonResponse, groupByField, isLoading, response, includeRawResponse, returnRawResponse]);
  const defaultRawResponses = [...(response ? [response] : []), ...(comparisonResponse ? [comparisonResponse] : [])];
  const data = returnRawResponse ? defaultRawResponses : parsedData;

  return {
    data,
    isLoading: isLoading || compareLoading
  };
}
